import { useEffect, useRef, useState } from 'react';
import { FEED_LATEST_POLL_MS, newFeedEventIds, newestFeedTime } from '../../shared/utils/feed-live';

type Page = LeagueFeed.Page & Partial<Pick<LeagueFeed.HomePage, 'context'>>;
export type FeedIncoming = { page: Page; ids: string[] };

type Options = {
  scope: string;
  url: string;
  events: LeagueFeed.Event[];
  hidden: Set<string>;
  // eslint-disable-next-line no-unused-vars -- callback signature
  readFirst: (signal: AbortSignal) => Promise<Page>;
  // Kullanıcı istekleri sürerken denetim beklemede kalır.
  paused: boolean;
};

/**
 * "Yeni gelişme var" denetimi (LG-55 ikinci dilim). Sekme görünürken dakikada bir en yeni olay
 * zamanı sorulur; yalnız daha yeni bir olay varsa ilk sayfa sessizce okunur. Kartlar kendiliğinden
 * eklenmez: okuma konumu bozulmasın diye kullanıcı düğmeye basınca gösterilir. Hata sessizdir;
 * elle yenileme her zaman açıktır.
 */
export default function useFeedIncoming({ scope, url, events, hidden, readFirst, paused }: Options) {
  const [incoming, setIncoming] = useState<(FeedIncoming & { scope: string }) | null>(null);
  const latest = useRef({ events, hidden, readFirst, paused, waiting: false });
  // Bekleyen yeni olay gösterilene kadar yeniden sorulmaz (Bluesky gibi; gereksiz istek yok).
  latest.current = { events, hidden, readFirst, paused, waiting: incoming?.scope === scope };

  useEffect(() => {
    let checked = 0;
    let lastRun = 0;
    let controller: AbortController | null = null;
    const check = async () => {
      if (
        document.visibilityState !== 'visible' ||
        controller ||
        latest.current.paused ||
        latest.current.waiting
      )
        return;
      lastRun = Date.now();
      const own = new AbortController();
      controller = own;
      try {
        const response = await fetch(url, { signal: own.signal });
        if (!response.ok) return;
        const remote = Number((await response.json())?.latest);
        if (!(remote > newestFeedTime(latest.current.events) && remote > checked)) return;
        checked = remote;
        const page = await latest.current.readFirst(own.signal);
        const ids = newFeedEventIds(page.events, latest.current.events, latest.current.hidden);
        if (!own.signal.aborted && ids.length) setIncoming({ page, ids, scope });
      } catch {
        // Sessiz: bir sonraki denetim ya da elle yenileme.
      } finally {
        if (controller === own) controller = null;
      }
    };
    const timer = window.setInterval(check, FEED_LATEST_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRun > FEED_LATEST_POLL_MS / 3)
        check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      controller?.abort();
    };
  }, [scope, url]);

  return {
    incoming: incoming?.scope === scope ? incoming : null,
    clearIncoming: () => setIncoming(null),
  };
}
